<div class="ai-report">
  <div class="ai-report-header">
    <div class="ai-report-toolbar">
      <div class="ai-report-toolbar-left">
        <div class="ai-toolbar-align">
          <div class="ai-toolbar-measure" aria-hidden="true">
            <span class="ai-toolbar-measure-back">
              <i class="fas fa-arrow-left"></i>
              Back
            </span>
            <span class="ai-toolbar-measure-tabs">
              <span class="ai-toolbar-measure-tab">
                <i class="fas fa-list"></i>
                Results
              </span>
              <span class="ai-toolbar-measure-tab">
                <i class="fas fa-wand-magic-sparkles"></i>
                AI OSINT Analysis
              </span>
            </span>
          </div>
          <div class="mode-tab-bar" role="tablist" aria-label="Analysis mode">
        <button
          type="button"
          role="tab"
          class="mode-tab"
          title="Quick analysis"
          [class.mode-tab-active]="analysisMode === 'quick'"
          [attr.aria-selected]="analysisMode === 'quick'"
          (click)="setAnalysisMode('quick')"
        >
          <mat-icon>bolt</mat-icon>
          <span class="mode-tab-label">Quick</span>
        </button>
        <button
          type="button"
          role="tab"
          class="mode-tab"
          title="Deep cheap analysis"
          [class.mode-tab-active]="analysisMode === 'deep-cheap'"
          [attr.aria-selected]="analysisMode === 'deep-cheap'"
          (click)="setAnalysisMode('deep-cheap')"
        >
          <mat-icon>layers</mat-icon>
          <span class="mode-tab-label">Deep cheap</span>
        </button>
        <button
          type="button"
          role="tab"
          class="mode-tab"
          title="Deep analysis"
          [class.mode-tab-active]="analysisMode === 'deep'"
          [attr.aria-selected]="analysisMode === 'deep'"
          (click)="setAnalysisMode('deep')"
        >
          <mat-icon>rocket_launch</mat-icon>
          <span class="mode-tab-label">Deep</span>
        </button>
          </div>
        </div>
      </div>

      <div class="ai-report-actions">
        <div class="ai-report-query" *ngIf="queryValue" [title]="queryValue">
          <mat-icon>search</mat-icon>
          <span>{{ queryValue }}</span>
        </div>

        <button
          mat-flat-button
          class="ai-generate-btn"
          type="button"
          (click)="generateAnalysis()"
          [disabled]="isGenerating || isPolling || isLoading || !hasSourceData"
        >
          <mat-icon>{{ hasAnalysis ? 'refresh' : 'bolt' }}</mat-icon>
          <span class="generate-btn-label">{{ hasAnalysis ? 'Regenerate' : 'Generate' }}</span>
        </button>
      </div>
    </div>
  </div>

  <div *ngIf="!hasSourceData" class="ai-empty-state">
    <mat-icon>info</mat-icon>
    <span>AI analysis becomes available once this search returns at least one OSINT result.</span>
  </div>

  <div *ngIf="hasSourceData && isLoading" class="ai-loading-state">
    <div class="spinner-border spinner-border-sm" role="status"></div>
    <span>{{ analysisMode === 'deep'
      ? 'Loading cached deep analysis...'
      : (analysisMode === 'deep-cheap' ? 'Loading cached deep-cheap analysis...' : 'Loading cached AI analysis...') }}</span>
  </div>

  <div *ngIf="hasSourceData && (isGenerating || isPolling)" class="ai-generating-state">
    <div class="ai-loader-shell" aria-hidden="true">
      <div class="ai-loader-core">
        <mat-icon>auto_awesome</mat-icon>
      </div>
      <span class="ai-loader-orbit orbit-one"></span>
      <span class="ai-loader-orbit orbit-two"></span>
      <span class="ai-loader-orbit orbit-three"></span>
    </div>

    <div class="ai-generating-copy">
      <div class="ai-generating-title">
        {{ isPolling
          ? 'Finishing AI analysis on the server'
          : (analysisMode === 'deep'
            ? 'Running deep AI analysis'
            : (analysisMode === 'deep-cheap' ? 'Running deep-cheap AI analysis' : 'Running AI analysis')) }}
      </div>
      <p class="ai-generating-text">
        {{ isPolling
          ? 'The request timed out at the edge, but the backend may still be completing the analysis. We are checking for the finished result automatically.'
          : (analysisMode === 'deep'
            ? 'Following linked profiles, public entities, and source evidence across a longer multi-step run.'
            : (analysisMode === 'deep-cheap'
              ? 'Checking live public traces with a tighter budget and building a richer summary.'
              : 'Reviewing the current OSINT result set and assembling an analyst-style summary.')) }}
      </p>

      <div class="ai-generating-steps" aria-hidden="true">
        <div class="ai-generating-step">
          <span class="step-dot"></span>
          <span>{{ isPolling ? 'Waiting for completion' : 'Linking identities' }}</span>
        </div>
        <div class="ai-generating-step">
          <span class="step-dot"></span>
          <span>{{ isPolling ? 'Checking stored result' : 'Checking source signals' }}</span>
        </div>
        <div class="ai-generating-step">
          <span class="step-dot"></span>
          <span>{{ isPolling ? 'Refreshing automatically' : 'Scoring confidence' }}</span>
        </div>
      </div>
    </div>
  </div>

  <div
    *ngIf="hasSourceData && !isLoading && !isGenerating && !isPolling && !hasAnalysis && !errorMessage"
    class="ai-empty-state"
  >
    <mat-icon>auto_awesome</mat-icon>
    <span>{{ analysisMode === 'deep'
      ? 'Generate a deeper, multi-step analyst summary with identity anchoring, linked profiles, entity enrichment, and source-backed next leads.'
      : (analysisMode === 'deep-cheap'
        ? 'Generate a lower-cost deep summary with live public web checks, tighter follow-up limits, and source-backed linked profiles.'
        : 'Generate an analyst-style summary, linked identities, related entities, and next leads from the current OSINT result set.') }}</span>
  </div>

  <div *ngIf="hasSourceData && !isGenerating && !isPolling && errorMessage" class="ai-error-state">
    <mat-icon>error_outline</mat-icon>
    <span>{{ errorMessage }}</span>
  </div>

  <div *ngIf="hasAnalysis" class="ai-analysis-body" [class.ai-analysis-body-loading]="isGenerating || isPolling">
    <div class="profile-hero-card shade-card" [ngClass]="profileHeroShadeClass">
      <div class="profile-hero-main">
        <div class="profile-avatar">{{ getSubjectInitials() }}</div>
        <div class="profile-hero-copy">
          <h2 class="profile-name">{{ displaySubjectName }}</h2>
          <ul class="colorful-point-list profile-point-list" *ngIf="heroSummaryPoints.length > 0">
            <li
              *ngFor="let point of heroSummaryPoints; let i = index"
              [ngClass]="getPointAccentClass(i)"
            >
              <span class="point-marker" aria-hidden="true"></span>
              <span class="point-text">{{ point }}</span>
            </li>
          </ul>
          <div class="profile-badges">
            <span class="attribution-pill" [ngClass]="attributionClass">{{ attributionLabel }}</span>
            <span class="confidence-pill" [ngClass]="confidenceClass">
              {{ analysisResponse?.analysis?.confidence | titlecase }} confidence
            </span>
            <span
              class="evidence-tier-pill evidence-tier-official"
              *ngIf="analysisResponse?.analysis?.primarySubject?.officialSignalPresent"
            >
              Official signal
            </span>
            <span
              class="evidence-tier-pill evidence-tier-linked"
              *ngIf="analysisResponse?.analysis?.primarySubject?.directQueryLinked"
            >
              Directly linked
            </span>
          </div>
        </div>
      </div>

      <div class="profile-stats-row">
        <div class="profile-stat">
          <span class="profile-stat-label">Mode</span>
          <span class="profile-stat-value">{{ activeModeLabel }}</span>
        </div>
        <div class="profile-stat">
          <span class="profile-stat-label">Confidence</span>
          <span class="profile-stat-value" [ngClass]="confidenceClass">
            {{ analysisResponse?.analysis?.confidence | titlecase }}
          </span>
        </div>
        <div class="profile-stat" *ngIf="analysisMode === 'deep' && socialProfilesCount > 0; else liveWebStat">
          <span class="profile-stat-label">Social profiles</span>
          <span class="profile-stat-value stat-positive">{{ socialProfilesCount }} found</span>
        </div>
        <ng-template #liveWebStat>
          <div class="profile-stat">
            <span class="profile-stat-label">Live web</span>
            <span class="profile-stat-value" [ngClass]="liveWebClass">{{ liveWebLabel }}</span>
          </div>
        </ng-template>
      </div>
    </div>

    <div class="feedback-panel" *ngIf="canUseFeedback">
      <div class="feedback-panel-header">
        <div class="feedback-panel-title">
          <mat-icon>forum</mat-icon>
          Human feedback
        </div>
        <div class="feedback-panel-subtitle">
          If the result looks wrong, tell the AI what is off or add more context before regenerating.
        </div>
      </div>

      <div class="feedback-chip-row" role="group" aria-label="Feedback type">
        <button
          mat-stroked-button
          type="button"
          class="feedback-chip feedback-chip-wrong"
          [class.feedback-chip-active]="feedbackStatus === 'wrong'"
          (click)="setFeedbackStatus('wrong')"
        >
          <mat-icon>close</mat-icon>
          Wrong result
        </button>
        <button
          mat-stroked-button
          type="button"
          class="feedback-chip feedback-chip-correct"
          [class.feedback-chip-active]="feedbackStatus === 'correct'"
          (click)="setFeedbackStatus('correct')"
        >
          <mat-icon>check</mat-icon>
          Correct
        </button>
        <button
          mat-stroked-button
          type="button"
          class="feedback-chip feedback-chip-review"
          [class.feedback-chip-active]="feedbackStatus === 'needs-review'"
          (click)="setFeedbackStatus('needs-review')"
        >
          <mat-icon>edit_note</mat-icon>
          Needs revision
        </button>
      </div>

      <textarea
        class="feedback-textarea"
        [class.feedback-textarea-wrong]="feedbackStatus === 'wrong'"
        [class.feedback-textarea-correct]="feedbackStatus === 'correct'"
        [class.feedback-textarea-review]="feedbackStatus === 'needs-review'"
        rows="3"
        [value]="feedbackNote"
        (input)="updateFeedbackNote($any($event.target).value)"
        placeholder="Add context, corrections, or extra signals the AI should consider..."
      ></textarea>

      <div class="feedback-actions">
        <button
          mat-stroked-button
          type="button"
          class="feedback-clear-button"
          (click)="clearFeedbackDraft()"
          [disabled]="isGenerating || isPolling || isLoading"
        >
          <mat-icon>backspace</mat-icon>
          Clear
        </button>
        <button
          mat-flat-button
          type="button"
          class="feedback-regenerate-button"
          (click)="generateAnalysis(true)"
          [disabled]="isGenerating || isPolling || isLoading || (!feedbackStatus && !feedbackNote.trim())"
        >
          <mat-icon>psychology</mat-icon>
          Regenerate with feedback
        </button>
      </div>

      <div class="feedback-cost-note">
        Regenerate with feedback calls AI again and deducts {{ regenerateWithFeedbackCostLabel }} on success.
      </div>

      <div class="saved-feedback" *ngIf="savedFeedback?.status || savedFeedback?.note">
        <div class="saved-feedback-header">
          <span class="saved-feedback-title">Saved analyst feedback</span>
        </div>
        <div class="saved-feedback-card" [ngClass]="getSavedFeedbackShadeClass(savedFeedback?.status)">
          <div class="saved-feedback-card-top">
            <span class="feedback-status-pill" [ngClass]="getSavedFeedbackPillClass(savedFeedback?.status)">
              {{ getSavedFeedbackStatusLabel(savedFeedback?.status) }}
            </span>
            <span class="saved-feedback-date" *ngIf="savedFeedback?.updated_at">
              {{ savedFeedback?.updated_at | date: 'medium' }}
            </span>
          </div>
          <p class="saved-feedback-note" *ngIf="savedFeedback?.note">{{ savedFeedback?.note }}</p>
        </div>
      </div>
    </div>

    <div class="ai-section section-caution" *ngIf="contradictionFlags.length > 0">
      <div class="section-heading section-heading-caution">
        <span class="section-accent"></span>
        Caution
      </div>
      <ul class="caution-list">
        <li *ngFor="let item of contradictionFlags">
          <mat-icon>warning_amber</mat-icon>
          <span>{{ item }}</span>
        </li>
      </ul>
    </div>

    <div class="ai-section section-summary" *ngIf="analysisResponse?.analysis?.executiveSummary">
      <div class="section-heading section-heading-summary">
        <span class="section-accent"></span>
        Summary
      </div>
      <div class="summary-panel">
        <ul class="colorful-point-list summary-point-list">
          <li
            *ngFor="let point of executiveSummaryPoints; let i = index"
            [ngClass]="getPointAccentClass(i)"
          >
            <span class="point-marker" aria-hidden="true"></span>
            <span class="point-text">{{ point }}</span>
          </li>
        </ul>
      </div>
    </div>

    <div class="ai-section section-entities" *ngIf="(analysisResponse?.analysis?.relatedEntities?.length || 0) > 0">
      <div class="section-heading section-heading-entities">
        <span class="section-accent"></span>
        Related entities
      </div>
      <div class="entity-grid entity-grid-cards">
        <div
          class="entity-card entity-card-icon shade-card"
          *ngFor="let entity of analysisResponse?.analysis?.relatedEntities; let i = index"
          [ngClass]="getEntityAccentClass(entity.type, i)"
        >
          <div class="entity-card-layout">
            <div class="entity-card-icon-wrap">
              <mat-icon>{{ getEntityIcon(entity.type) }}</mat-icon>
            </div>
            <div class="entity-card-body">
              <div class="entity-type">{{ getEntityLabel(entity.type) }}</div>
              <div class="entity-value" [title]="entity.value">{{ entity.value }}</div>
              <div class="entity-context" *ngIf="entity.context">{{ entity.context }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="ai-section section-details" *ngIf="(analysisResponse?.analysis?.detailBlocks?.length || 0) > 0">
      <div class="section-heading section-heading-details">
        <span class="section-accent"></span>
        {{ analysisMode === 'quick' ? 'Phone details' : 'Detailed entity analysis' }}
      </div>
      <div class="detail-block-stack">
        <div
          class="detail-block-card shade-card"
          *ngFor="let block of analysisResponse?.analysis?.detailBlocks"
          [ngClass]="getCardShadeClass(block.confidence)"
        >
          <div class="detail-block-header">
            <div class="detail-block-title">
              <mat-icon>{{ getEntityIcon(block.entityType) }}</mat-icon>
              <span>{{ block.sectionTitle }}</span>
            </div>
            <span class="confidence-pill" [ngClass]="getSocialProfileConfidenceClass(block.confidence)">
              {{ block.confidence | titlecase }}
            </span>
          </div>

          <p class="entity-detail-summary" *ngIf="block.summary">{{ block.summary }}</p>

          <div class="detail-kv-grid" *ngIf="(block.analysisBullets?.length || 0) > 0">
            <div class="detail-kv-row" *ngFor="let bullet of block.analysisBullets">
              <span class="detail-kv-key">{{ bullet.label }}</span>
              <span class="detail-kv-value" [title]="bullet.detail">{{ bullet.detail }}</span>
            </div>
          </div>

          <div class="detail-sources" *ngIf="block.sourceReferences.length > 0">
            <div class="social-block-title">Sources</div>
            <ul class="source-link-list">
              <li *ngFor="let source of block.sourceReferences">
                <ng-container *ngIf="getSourceUrl(source) as sourceUrl; else plainBlockSource">
                  <a
                    [href]="sourceUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="social-link"
                    [title]="getSourceLabel(source) !== sourceUrl ? getSourceLabel(source) : sourceUrl"
                  >
                    <mat-icon>open_in_new</mat-icon>
                    <span class="truncate-ellipsis">{{ getSourceLabel(source) !== sourceUrl ? getSourceLabel(source) : sourceUrl }}</span>
                  </a>
                </ng-container>
                <ng-template #plainBlockSource>{{ source }}</ng-template>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <div class="ai-section section-findings" *ngIf="(analysisResponse?.analysis?.keyFindings?.length || 0) > 0">
      <div class="section-heading section-heading-findings">
        <span class="section-accent"></span>
        Key finding{{ (analysisResponse?.analysis?.keyFindings?.length || 0) > 1 ? 's' : '' }}
      </div>
      <div class="finding-stack">
        <div class="finding-card shade-card status-shade-findings" *ngFor="let finding of analysisResponse?.analysis?.keyFindings">
          <div class="finding-card-body">
            <div class="finding-title">
              <mat-icon>link</mat-icon>
              <strong>{{ finding.title }}</strong>
            </div>
            <p>{{ finding.detail }}</p>
            <ul class="evidence-list" *ngIf="finding.evidence.length">
              <li *ngFor="let evidence of finding.evidence">{{ evidence }}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <div
      class="ai-section section-social"
      *ngIf="(analysisResponse?.analysis?.socialProfiles?.length || 0) > 0"
    >
      <div class="section-heading section-heading-social">
        <span class="section-accent"></span>
        Social profiles ({{ socialProfilesCount }} found)
      </div>
      <div class="social-profile-grid">
        <div
          class="social-platform-card shade-card"
          *ngFor="let profile of analysisResponse?.analysis?.socialProfiles; let i = index"
          [ngClass]="getPlatformBrandShadeClass(profile.platform)"
        >
          <div class="social-platform-header">
            <div class="social-platform-brand">
              <div class="social-platform-icon">
                <i [class]="getPlatformBrandIconClass(profile.platform)" aria-hidden="true"></i>
              </div>
              <div>
                <div class="social-platform-name">{{ getPlatformTitle(profile.platform) }}</div>
                <div class="social-platform-handle">{{ getDisplayHandle(profile.handle, profile.platform) }}</div>
              </div>
            </div>
            <span class="confidence-pill" [ngClass]="getSocialProfileConfidenceClass(profile.confidence)">
              {{ profile.confidence | titlecase }}
            </span>
          </div>

          <p class="social-platform-assessment" *ngIf="profile.profileAssessment || profile.contentThemeSummary">
            {{ profile.profileAssessment || profile.contentThemeSummary }}
          </p>

          <ul class="social-evidence-bullets" *ngIf="profile.evidence.length > 0">
            <li *ngFor="let evidence of profile.evidence">{{ evidence }}</li>
          </ul>

          <ul class="social-analysis-bullets" *ngIf="(profile.analysisBullets?.length || 0) > 0 && profile.evidence.length === 0">
            <li *ngFor="let bullet of profile.analysisBullets">
              <strong>{{ bullet.label }}:</strong>
              <span>{{ bullet.detail }}</span>
            </li>
          </ul>

          <div class="social-profile-meta">
            <span *ngIf="profile.displayName">{{ profile.displayName }}</span>
            <span *ngIf="profile.profileType">{{ profile.profileType }}</span>
            <a
              *ngIf="profile.profileUrl"
              [href]="profile.profileUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="social-link"
            >
              <mat-icon>open_in_new</mat-icon>
              Open profile
            </a>
          </div>
        </div>
      </div>
    </div>

    <div
      class="ai-section section-identity"
      *ngIf="(analysisResponse?.analysis?.identityCandidates?.length || 0) > 0"
    >
      <div class="section-heading section-heading-identity">
        <span class="section-accent"></span>
        Identity candidates ({{ analysisResponse?.analysis?.identityCandidates?.length }})
      </div>
      <div class="identity-stack">
        <div
          class="identity-card shade-card"
          *ngFor="let candidate of analysisResponse?.analysis?.identityCandidates; let i = index"
          [ngClass]="getCardShadeClass(candidate.confidence, candidate.attributionStatus)"
        >
          <div class="identity-card-header">
            <strong>{{ candidate.name }}</strong>
            <span class="confidence-pill" [ngClass]="getSocialProfileConfidenceClass(candidate.confidence)">
              {{ candidate.confidence | titlecase }}
            </span>
          </div>
          <p>{{ candidate.description }}</p>
          <ul class="evidence-list" *ngIf="candidate.evidence.length">
            <li *ngFor="let evidence of candidate.evidence">{{ evidence }}</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="ai-section" *ngIf="confirmedEvidenceBlocks.length > 0">
      <div class="section-heading section-heading-identity">
        <span class="section-accent"></span>
        Confirmed evidence
      </div>
      <div class="detail-block-stack">
        <div
          class="detail-block-card shade-card"
          *ngFor="let block of confirmedEvidenceBlocks"
          [ngClass]="getCardShadeClass(block.confidence, 'confirmed')"
        >
          <div class="detail-block-header">
            <div class="detail-block-title">
              <mat-icon>verified</mat-icon>
              <span>{{ block.sectionTitle }}</span>
            </div>
            <div class="profile-header-pills">
              <span class="evidence-tier-pill" *ngIf="block.evidenceTier">{{ getEvidenceTierLabel(block.evidenceTier) }}</span>
              <span class="confidence-pill" [ngClass]="getSocialProfileConfidenceClass(block.confidence)">{{ block.confidence | titlecase }}</span>
            </div>
          </div>
          <p class="entity-detail-summary" *ngIf="block.summary">{{ block.summary }}</p>
          <ul class="social-analysis-bullets" *ngIf="(block.analysisBullets?.length || 0) > 0">
            <li *ngFor="let bullet of block.analysisBullets">
              <strong>{{ bullet.label }}:</strong>
              <span>{{ bullet.detail }}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div class="ai-section" *ngIf="crossPlatformCorroboration.length > 0">
      <div class="section-heading section-heading-summary">
        <span class="section-accent"></span>
        Cross-platform corroboration
      </div>
      <ul class="evidence-list">
        <li *ngFor="let item of crossPlatformCorroboration">{{ item }}</li>
      </ul>
    </div>

    <div class="ai-section" *ngIf="strongCandidateProfiles.length > 0">
      <div class="section-heading section-heading-social">
        <span class="section-accent"></span>
        Strong candidate profiles
      </div>
      <div class="entity-grid entity-grid-cards">
        <div
          class="entity-card entity-card-icon shade-card"
          *ngFor="let profile of strongCandidateProfiles"
          [ngClass]="profile.platform ? getPlatformBrandShadeClass(profile.platform) : 'status-shade-candidate'"
        >
          <div class="entity-card-layout">
            <div class="entity-card-icon-wrap">
              <i
                *ngIf="profile.platform; else strongCandidateFallbackIcon"
                [class]="getPlatformBrandIconClass(profile.platform)"
                aria-hidden="true"
              ></i>
              <ng-template #strongCandidateFallbackIcon>
                <mat-icon>person_search</mat-icon>
              </ng-template>
            </div>
            <div class="entity-card-body">
              <div class="entity-type">Possible candidate</div>
              <div class="entity-value">{{ profile.sectionTitle || getDisplayHandle(profile.handle, profile.platform) }}</div>
              <div class="entity-context">{{ profile.profileAssessment || profile.contentThemeSummary || profile.biography }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="ai-section" *ngIf="supportingRelatedProfiles.length > 0">
      <div class="section-heading section-heading-entities">
        <span class="section-accent"></span>
        Supporting related profiles
      </div>
      <div class="entity-grid entity-grid-cards">
        <div class="entity-card entity-card-icon shade-card status-shade-supporting" *ngFor="let profile of supportingRelatedProfiles">
          <div class="entity-card-layout">
            <div class="entity-card-icon-wrap">
              <mat-icon>group</mat-icon>
            </div>
            <div class="entity-card-body">
              <div class="entity-type">Supporting profile</div>
              <div class="entity-value">{{ profile.sectionTitle || getDisplayHandle(profile.handle, profile.platform) }}</div>
              <div class="entity-context">{{ profile.profileAssessment || profile.contentThemeSummary || profile.biography }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="ai-section" *ngIf="officialProfessionalEvidence.length > 0">
      <div class="section-heading section-heading-findings">
        <span class="section-accent"></span>
        Official / professional evidence
      </div>
      <div class="detail-block-stack">
        <ng-container *ngFor="let item of officialProfessionalEvidence; let i = index">
          <div
            class="detail-block-card shade-card"
            *ngIf="isProfileEvidence(item); else officialEvidenceBlock"
            [ngClass]="getCardShadeClass(item.confidence, item.profileRole)"
          >
            <div class="detail-block-header">
              <div class="detail-block-title">
                <mat-icon>work</mat-icon>
                <span>{{ getEvidenceTitle(item, i) }}</span>
              </div>
              <div class="profile-header-pills">
                <span class="evidence-tier-pill" *ngIf="item.evidenceTier">{{ getEvidenceTierLabel(item.evidenceTier) }}</span>
                <span class="confidence-pill" [ngClass]="getSocialProfileConfidenceClass(item.confidence)">{{ item.confidence | titlecase }}</span>
              </div>
            </div>
            <p class="entity-detail-summary" *ngIf="getEvidenceSummary(item)">{{ getEvidenceSummary(item) }}</p>
          </div>
          <ng-template #officialEvidenceBlock>
            <div
              class="detail-block-card shade-card"
              [ngClass]="getCardShadeClass(item.confidence, item.evidenceTier === 'tier1' ? 'confirmed' : item.profileRole)"
            >
              <div class="detail-block-header">
                <div class="detail-block-title">
                  <mat-icon>gavel</mat-icon>
                  <span>{{ getEvidenceTitle(item, i) }}</span>
                </div>
                <div class="profile-header-pills">
                  <span class="evidence-tier-pill" *ngIf="item.evidenceTier">{{ getEvidenceTierLabel(item.evidenceTier) }}</span>
                  <span class="confidence-pill" [ngClass]="getSocialProfileConfidenceClass(item.confidence)">{{ item.confidence | titlecase }}</span>
                </div>
              </div>
              <p class="entity-detail-summary" *ngIf="getEvidenceSummary(item)">{{ getEvidenceSummary(item) }}</p>
            </div>
          </ng-template>
        </ng-container>
      </div>
    </div>

    <div class="ai-section" *ngIf="publicSocialEvidence.length > 0">
      <div class="section-heading section-heading-social">
        <span class="section-accent"></span>
        Public social evidence
      </div>
      <div class="detail-block-stack">
        <div
          class="detail-block-card shade-card"
          *ngFor="let profile of publicSocialEvidence; let i = index"
          [ngClass]="getPlatformBrandShadeClass(profile.platform)"
        >
          <div class="detail-block-header">
            <div class="detail-block-title">
              <i [class]="getPlatformBrandIconClass(profile.platform)" aria-hidden="true"></i>
              <span>{{ getEvidenceTitle(profile, i) }}</span>
            </div>
            <div class="profile-header-pills">
              <span class="attribution-pill attribution-supporting">{{ getProfileRoleLabel(profile.profileRole) }}</span>
              <span class="evidence-tier-pill" *ngIf="profile.evidenceTier">{{ getEvidenceTierLabel(profile.evidenceTier) }}</span>
              <span class="confidence-pill" [ngClass]="getSocialProfileConfidenceClass(profile.confidence)">{{ profile.confidence | titlecase }}</span>
            </div>
          </div>
          <p class="entity-detail-summary" *ngIf="getEvidenceSummary(profile)">{{ getEvidenceSummary(profile) }}</p>
          <div class="social-profile-meta">
            <span *ngIf="profile.displayName">{{ profile.displayName }}</span>
            <a
              *ngIf="profile.profileUrl"
              [href]="profile.profileUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="social-link"
            >
              <mat-icon>open_in_new</mat-icon>
              Open profile
            </a>
          </div>
        </div>
      </div>
    </div>

    <div class="ai-section section-limitations" *ngIf="limitations.length > 0">
      <div class="section-heading section-heading-limitations">
        <span class="section-accent"></span>
        Limitations
      </div>
      <ul class="limitations-list">
        <li *ngFor="let item of limitations">
          <mat-icon>info</mat-icon>
          <span>{{ item }}</span>
        </li>
      </ul>
    </div>

    <div class="ai-section section-sources" *ngIf="(analysisResponse?.analysis?.sourceReferences?.length || 0) > 0">
      <div class="section-heading section-heading-sources">
        <span class="section-accent"></span>
        Sources used
      </div>
      <ul class="source-chip-list">
        <li *ngFor="let source of analysisResponse?.analysis?.sourceReferences">
          <ng-container *ngIf="getSourceUrl(source) as sourceUrl; else plainTopSource">
            <a [href]="sourceUrl" target="_blank" rel="noopener noreferrer" class="source-chip" [title]="getSourceLabel(source) !== sourceUrl ? getSourceLabel(source) : sourceUrl">
              <mat-icon>open_in_new</mat-icon>
              <span>{{ getSourceLabel(source) !== sourceUrl ? getSourceLabel(source) : sourceUrl }}</span>
            </a>
          </ng-container>
          <ng-template #plainTopSource>
            <span class="source-chip source-chip-plain" [title]="source">{{ source }}</span>
          </ng-template>
        </li>
      </ul>
    </div>

    <div class="ai-report-footer" *ngIf="analysisResponse?.generated_at">
      <span>Generated {{ analysisResponse?.generated_at | date: 'medium' }}</span>
      <span *ngIf="analysisResponse?.model"> · {{ analysisResponse?.model }}</span>
    </div>
  </div>
</div>
